import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BASE_URL } from '../utils';
import { verifySession } from '../utils/verifySession';

const defaultPermissions = {
  pages: {
    todayView: true,
    filters: true,
    lab: true,
    emr: true,
    crm: true,
    taskManagement: true,
    documentIntelligence: true,
    dailyCommandCenter: true,
    adminLogs: false,

    frontDesk: true,
    admin: true,
    roomView: true,
    auditView: true,
    exportData: true,
    patientSummary: true,
    treatmentView: true,
    patientForms: true,
    schedule: true,
    rulesToFollow: true,
    contacts: true,
    // Old Views - disabled by default
    todayViewOld: false,
    treatmentViewOld: false,
    auditViewOld: false,
    emrOld: false,
  },
  adminFeatures: {
    patientManagement: true,
    labsVendors: true,
    procedures: true,
    calendars: true,
    dispensing: true,
    supplements: true,
    rooms: true,
    assignees: true,
    treatmentTemplates: true,
    templates: true,
    emailTemplates: true,
    additionalResources: true,
  },
};

// Maps a route pathname to the permissions key
export const ROUTE_PERMISSION_MAP = {
  '/': 'todayView',
  '/todayview': 'todayView',
  '/todayviewold': 'todayViewOld',  // Separate key for old view
  '/filters': 'filters',
  '/labtracker': 'lab',
  '/emr': 'emr',
  '/crm': 'crm',
  '/task-management': 'taskManagement',
  '/document-intelligence': 'documentIntelligence',
  '/daily-command-center': 'dailyCommandCenter',
  '/admin-logs': 'adminLogs',
  '/old-emr': 'emrOld',
  '/frontdesk': 'frontDesk',
  '/updations': 'admin',
  '/roomview': 'roomView',
  '/auditview': 'auditView',
  '/exportdata': 'exportData',
  '/patientsummary': 'patientSummary',
  '/treatmentview': 'treatmentView',
  '/treatmentviewdisplay': 'treatmentView',
  '/patient-forms': 'patientForms',
  '/patientform': 'patientForms',
  '/patientformold': 'patientForms',
};

const PermissionsContext = createContext({
  permissions: defaultPermissions,
  permissionsLoaded: false,
  isAdmin: false,
  hasUserOverrides: false,
  hasPageAccess: (_key) => true,
  hasAdminFeatureAccess: (_key) => true,
  reloadPermissions: () => {},
});

let permissionsRequest = null;
let permissionsRequestKey = null;

export function PermissionsProvider({ children }) {
  const [permissions, setPermissions] = useState(defaultPermissions);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasUserOverrides, setHasUserOverrides] = useState(false);

  const loadPermissions = useCallback(async () => {
    const token =
      localStorage.getItem('cognitoAccessToken') ||
      localStorage.getItem('sessionToken');
    const idToken = localStorage.getItem('cognitoIdToken');

    if (!token) {
      setPermissions(defaultPermissions);
      setIsAdmin(false);
      setHasUserOverrides(false);
      setPermissionsLoaded(true);
      return;
    }

    // We have a token — mark not-loaded while we fetch so guards wait
    setPermissionsLoaded(false);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (idToken) headers['x-id-token'] = idToken;

      // The app shell's /verify-token call embeds the /my-permissions body, so the
      // first load needs one request. Fall back to /my-permissions when the
      // payload is missing or the verify call fails.
      let res = await verifySession(token, idToken)
        .then((verifyRes) => (verifyRes.data?.myPermissions ? { data: verifyRes.data.myPermissions } : null))
        .catch(() => null);

      if (!res) {
        const requestKey = `${token}:${idToken || ''}`;
        if (!permissionsRequest || permissionsRequestKey !== requestKey) {
          permissionsRequestKey = requestKey;
          permissionsRequest = axios
            .get(`${BASE_URL}/my-permissions`, { headers })
            .finally(() => {
              permissionsRequest = null;
              permissionsRequestKey = null;
            });
        }

        res = await permissionsRequest;
      }

      if (res.data?.success) {
        setPermissions(res.data.permissions || defaultPermissions);
        setIsAdmin(res.data.isAdmin === true);
        setHasUserOverrides(res.data.hasUserOverrides === true);
      }
    } catch (err) {
      // On error fall back to full access so users aren't unexpectedly locked out
      console.warn('[PermissionsContext] Failed to load permissions, defaulting to full access:', err.message);
      setPermissions(defaultPermissions);
      setHasUserOverrides(false);
    } finally {
      setPermissionsLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadPermissions();

    // Reload when any tab sets a new token (cross-tab login)
    const handleStorage = (e) => {
      if (e.key === 'cognitoAccessToken' || e.key === 'sessionToken' || e.key === 'cognitoIdToken') {
        loadPermissions();
      }
    };

    // Reload on explicit event dispatched by login page (same-tab login)
    const handleReload = () => loadPermissions();

    window.addEventListener('storage', handleStorage);
    window.addEventListener('permissions:reload', handleReload);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('permissions:reload', handleReload);
    };
  }, [loadPermissions]);

  const hasPageAccess = useCallback(
    (key) => {
      if (isAdmin && !hasUserOverrides) return true;
      return permissions?.pages?.[key] !== false;
    },
    [permissions, isAdmin, hasUserOverrides]
  );

  const hasAdminFeatureAccess = useCallback(
    (key) => {
      if (isAdmin && !hasUserOverrides) return true;
      return permissions?.adminFeatures?.[key] !== false;
    },
    [permissions, isAdmin, hasUserOverrides]
  );

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        permissionsLoaded,
        isAdmin,
        hasUserOverrides,
        hasPageAccess,
        hasAdminFeatureAccess,
        reloadPermissions: loadPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionsContext);
}

export { defaultPermissions };
